import { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
} from '@mui/material';
import { FilestoreFile } from '../api/filestore';
import { useUnitSystem } from '../hooks/useUnitSystem';
import { feetToMeters } from '../utils/units';
import { FeatureWidthConfig } from '../utils/geojsonUtils';

interface WidthSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (widthConfig: WidthConfig) => void;
  file: FilestoreFile;
  fields: string[];
  geojson: any;
  /** What kind of features need a width, e.g. "line", "point", "line and point". */
  featureKindLabel?: string;
}

/** Widths handed back by the modal are always in metres. */
export type WidthConfig = FeatureWidthConfig;

const WidthSelectionModal: React.FC<WidthSelectionModalProps> = ({
  open,
  onClose,
  onApply,
  file,
  fields,
  geojson,
  featureKindLabel = 'line',
}) => {
  const unitSystem = useUnitSystem();
  const widthUnit = unitSystem === 'imperial' ? 'ft' : 'm';
  const [mode, setMode] = useState<'manual' | 'field'>('manual');
  const [width, setWidth] = useState<string>('10');
  const [selectedField, setSelectedField] = useState<string>('');
  const [errors, setErrors] = useState<{ width?: string; field?: string }>({});
  const [fieldSamples, setFieldSamples] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      setMode('manual');
      setWidth('10');
      setSelectedField('');
      setErrors({});
      setFieldSamples([]);
    }
  }, [open]);

  useEffect(() => {
    if (mode === 'field' && selectedField && geojson) {
      const samples = collectFieldSamples(geojson, selectedField, 3);
      setFieldSamples(samples);
    } else {
      setFieldSamples([]);
    }
  }, [mode, selectedField, geojson]);

  const collectFieldSamples = (data: any, field: string, limit: number = 3): string[] => {
    if (!data || !field) {
      return [];
    }

    const collections = Array.isArray(data) ? data : [data];
    const samples: string[] = [];
    const seen = new Set<string>();

    for (const collection of collections) {
      if (!collection?.features || !Array.isArray(collection.features)) {
        continue;
      }
      for (const feature of collection.features) {
        const value = feature?.properties?.[field];
        if (value === undefined || value === null) {
          continue;
        }
        const asString = String(value);
        if (!seen.has(asString)) {
          seen.add(asString);
          samples.push(asString);
          if (samples.length >= limit) {
            return samples;
          }
        }
      }
    }

    return samples;
  };

  const validate = () => {
    const newErrors: { width?: string; field?: string } = {};

    if (mode === 'manual') {
      if (!width || isNaN(Number(width)) || Number(width) <= 0) {
        newErrors.width = 'Width must be a positive number';
      }
    } else if (mode === 'field') {
      if (!selectedField) {
        newErrors.field = 'Please select a field';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleApply = () => {
    if (!validate()) {
      return;
    }

    const manualWidthMeters =
      unitSystem === 'imperial' ? feetToMeters(Number(width)) : Number(width);
    const config: WidthConfig = {
      mode,
      ...(mode === 'manual' ? { width: manualWidthMeters } : { field: selectedField }),
    };

    onApply(config);
    onClose();
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          minWidth: 500,
          maxWidth: 600,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Configure Line Width
        </Typography>
        
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          The file "{file.original_filename}" contains {featureKindLabel} features that require a width specification.
          Please specify how the width should be determined.
        </Typography>

        <FormControl component="fieldset" sx={{ width: '100%', mb: 3 }}>
          <RadioGroup
            value={mode}
            onChange={(e) => setMode(e.target.value as 'manual' | 'field')}
          >
            <FormControlLabel
              value="manual"
              control={<Radio />}
              label={`Apply the same width to all ${featureKindLabel} features`}
            />
            <FormControlLabel
              value="field"
              control={<Radio />}
              label="Use width values from a field in the file"
              disabled={fields.length === 0}
            />
          </RadioGroup>
        </FormControl>

        {mode === 'manual' && (
          <TextField
            label={`Width (${widthUnit})`}
            type="number"
            fullWidth
            value={width}
            onChange={(e) => setWidth(e.target.value)}
            error={!!errors.width}
            helperText={
              errors.width ||
              `Enter the width in ${
                unitSystem === 'imperial' ? 'feet' : 'meters'
              } for all ${featureKindLabel} features`
            }
            sx={{ mb: 3 }}
            inputProps={{ min: 0.1, step: 0.1 }}
          />
        )}

        {mode === 'field' && (
          <>
            <FormControl fullWidth sx={{ mb: 2 }} error={!!errors.field}>
              <InputLabel>Width Field</InputLabel>
              <Select
                value={selectedField}
                label="Width Field"
                onChange={(e) => setSelectedField(e.target.value)}
              >
                {fields.map((field) => (
                  <MenuItem key={field} value={field}>
                    {field}
                  </MenuItem>
                ))}
              </Select>
              {errors.field && (
                <FormHelperText error>{errors.field}</FormHelperText>
              )}
              <FormHelperText>Field values are interpreted as meters.</FormHelperText>
            </FormControl>

            {fields.length === 0 && (
              <FormHelperText error sx={{ mb: 2 }}>
                No properties were found in the uploaded file.
              </FormHelperText>
            )}

            {fieldSamples.length > 0 ? (
              <FormHelperText sx={{ mb: 2 }}>
                Sample values: {fieldSamples.join(', ')}
              </FormHelperText>
            ) : (
              selectedField && (
                <FormHelperText sx={{ mb: 2 }}>
                  No values found for this field.
                </FormHelperText>
              )
            )}
          </>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleApply} variant="contained" color="primary">
            Apply
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default WidthSelectionModal;
